import { createContext, useContext, useEffect, useState } from "react";
import * as authApi from "../api/auth.api";
import { setAccessToken as setApiAccessToken } from "../api/client";
import i18n from "../i18n";

const AuthContext = createContext(null);

const SUPPORTED_LANGUAGES = ["de", "en"];

function applyPreferredLanguage(lang) {
  if (!lang || !SUPPORTED_LANGUAGES.includes(lang)) {
    return;
  }
  if (i18n.resolvedLanguage !== lang) {
    i18n.changeLanguage(lang);
  }
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem("dashboardLanguage", lang);
    } catch {
      /* ignore */
    }
  }
}

export function AuthProvider({ children }) {
  const [accessToken, setAccessToken] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = async () => {
    try {
      const me = await authApi.getMe();
      setUser(me);
      return me;
    } catch {
      return null;
    }
  };

  // 🔄 Beim App-Start: Refresh nur versuchen, wenn der Nutzer fr\u00fcher
  // schonmal eingeloggt war. Vermeidet den 401-Console-Noise auf der
  // Loginseite f\u00fcr neue Besucher.
  useEffect(() => {
    let hadSession = false;
    try {
      hadSession = window.localStorage.getItem("hadSession") === "true";
    } catch {
      /* ignore */
    }

    if (!hadSession) {
      setLoading(false);
      return;
    }

    authApi
      .refresh()
      .then(async (data) => {
        setAccessToken(data.accessToken);        // React-State
        setApiAccessToken(data.accessToken);     // API-Client
        try {
          const me = await authApi.getMe();
          setUser(me);
          applyPreferredLanguage(me?.preferred_language);
        } catch {
          /* ignore */
        }
      })
      .catch(() => {
        setAccessToken(null);
        setApiAccessToken(null);
        setUser(null);
        try {
          window.localStorage.removeItem("hadSession");
        } catch {
          /* ignore */
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const data = await authApi.login(email, password);
    setAccessToken(data.accessToken);            // React-State
    setApiAccessToken(data.accessToken);         // API-Client

    // Bei einem frischen Login soll das Lieblings-Unternehmen wieder
    // aktiv werden. Eine evtl. zuvor gespeicherte Auswahl wird verworfen,
    // damit der CompanyContext beim Laden auf das Favoriten-Feld zurueckgreift.
    try {
      window.localStorage.removeItem("selectedCompanyId");
      window.localStorage.setItem("hadSession", "true");
    } catch {
      /* ignore */
    }

    try {
      const me = await authApi.getMe();
      setUser(me);
      applyPreferredLanguage(me?.preferred_language);
    } catch {
      /* ignore */
    }
  };

  // Schliesst den OAuth-Login ab: Einmal-Code -> Access-Token + Session.
  const loginWithOAuthCode = async (code) => {
    const data = await authApi.oauthExchange(code);
    setAccessToken(data.accessToken);
    setApiAccessToken(data.accessToken);
    try {
      window.localStorage.removeItem("selectedCompanyId");
      window.localStorage.setItem("hadSession", "true");
    } catch {
      /* ignore */
    }
    try {
      const me = await authApi.getMe();
      setUser(me);
      applyPreferredLanguage(me?.preferred_language);
    } catch {
      /* ignore */
    }
  };

  const logout = async () => {
    await authApi.logout();
    setAccessToken(null);
    setApiAccessToken(null);
    setUser(null);
    try {
      window.localStorage.removeItem("hadSession");
    } catch {
      /* ignore */
    }
  };

  const localLogout = () => {
    // Nur lokalen State bereinigen, ohne Backend-Call
    setAccessToken(null);
    setApiAccessToken(null);
    setUser(null);
    try {
      window.localStorage.removeItem("hadSession");
    } catch {
      /* ignore */
    }
  };

  return (
    <AuthContext.Provider
      value={{
        accessToken,
        isAuthenticated: !!accessToken,
        user,
        refreshUser,
        login,
        loginWithOAuthCode,
        logout,
        localLogout,
        applyPreferredLanguage,
      }}
    >
      {!loading && children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}